import * as React from 'react';
import { useState } from 'react';
import { Trophy, Target, Clock, Gift, ChevronRight } from 'lucide-react';
import type { Quest, QuestObjective } from '../../../../types/features';
import { questService } from '../../../../features/quests/questService';
import { QuestDetailsModal } from './QuestDetailsModal';

export const QuestsPanel: React.FC = () => {
    const activeQuests = questService.getActiveQuests();
    const [selectedQuest, setSelectedQuest] = useState<Quest | null>(null);

    const getProgressPercentage = (quest: Quest) => {
        const objective = quest.objectives[0]; // For now, handling single objective
        return Math.min((objective.currentProgress / objective.threshold) * 100, 100);
    };

    const formatReward = (quest: Quest) => {
        const { type, amount } = quest.reward;
        switch (type) {
            case 'game_coins':
                return `${amount.toLocaleString()} Coins`;
            case 'exp':
                return `${amount.toLocaleString()} XP`;
            case 'free_spins':
                return `${amount} Free Spins`;
            case 'money':
                return amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
            default:
                return '';
        }
    };

    const getTimeLeft = (endDate: string) => {
        const end = new Date(endDate);
        const now = new Date();
        const diff = end.getTime() - now.getTime();

        if (diff <= 0) return 'Expired';

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (days > 0) return `${days}d ${hours}h left`;
        return `${hours}h left`;
    };

    return (
        <>
            <div className="space-y-4">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Trophy className="w-6 h-6 text-yellow-500" />
                        Active Quests
                    </h2>
                </div>

                <div className="grid gap-4">
                    {activeQuests.map((quest) => (
                        <div
                            key={quest.id}
                            className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow cursor-pointer"
                            onClick={() => setSelectedQuest(quest)}
                        >
                            {quest.bannerUrl && (
                                <div className="relative h-32 bg-gray-100">
                                    <img
                                        src={quest.bannerUrl}
                                        alt={quest.name}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                    <div className="absolute bottom-3 left-3 right-3">
                                        <h3 className="text-white text-lg font-bold truncate">{quest.name}</h3>
                                    </div>
                                </div>
                            )}

                            <div className="p-4">
                                <p className="text-gray-600 text-sm mb-3">{quest.description}</p>

                                {quest.objectives.map((objective: QuestObjective) => (
                                    <div key={objective.id} className="space-y-2 mb-4">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600 flex items-center gap-1.5">
                                                <Target className="w-4 h-4 text-blue-500" />
                                                {objective.description}
                                            </span>
                                            <span className="font-medium">
                                                {objective.currentProgress}/{objective.threshold}
                                            </span>
                                        </div>
                                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-blue-500 transition-all duration-300"
                                                style={{ width: `${getProgressPercentage(quest)}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}

                                <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-1.5 text-gray-500">
                                        <Clock className="w-4 h-4" />
                                        {getTimeLeft(quest.endDate)}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-emerald-600 font-medium">
                                        <Gift className="w-4 h-4" />
                                        {formatReward(quest)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {activeQuests.length === 0 && (
                    <div className="text-center py-8">
                        <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No active quests available</p>
                    </div>
                )}
            </div>

            {selectedQuest && (
                <QuestDetailsModal
                    quest={selectedQuest}
                    onClose={() => setSelectedQuest(null)}
                />
            )}
        </>
    );
}; 